const PREFIX = 'byonco:member:v1';
const PENDING_PREFIX = 'byonco:pending-docs';

function empty() {
  return {
    conversations: [],
    messages: {},
    bookmarks: [],
    checkins: [],
    analyses: [],
  };
}

function userScope(user) {
  const id = user && (user.id || user.user_id || user.email);
  if (!id) return '';
  return String(id).trim().toLowerCase().replace(/[^a-z0-9@._-]/g, '').slice(0, 120);
}

function storageKey(user) {
  const scope = userScope(user);
  return scope ? `${PREFIX}:${scope}` : '';
}

function pendingKey(user) {
  const scope = userScope(user);
  return scope ? `${PENDING_PREFIX}:${scope}` : '';
}

function read(user) {
  if (typeof window === 'undefined') return empty();
  const key = storageKey(user);
  if (!key) return empty();
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || '{}');
    return {
      conversations: parsed.conversations || [],
      messages: parsed.messages || {},
      bookmarks: parsed.bookmarks || [],
      checkins: parsed.checkins || [],
      analyses: parsed.analyses || [],
    };
  } catch {
    return empty();
  }
}

function write(user, data) {
  if (typeof window === 'undefined') return;
  const key = storageKey(user);
  if (!key) return;
  window.localStorage.setItem(key, JSON.stringify(data));
}

export function clearMemberLocalData(user) {
  if (typeof window === 'undefined') return;
  const key = storageKey(user);
  const docs = pendingKey(user);
  if (key) window.localStorage.removeItem(key);
  if (docs) window.sessionStorage.removeItem(docs);
}

/** Wipe every member cache on this browser. Call on logout. */
export function clearAllMemberLocalData() {
  if (typeof window === 'undefined') return;
  const keys = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (key && (key === PREFIX || key.startsWith(`${PREFIX}:`))) keys.push(key);
  }
  keys.forEach((key) => window.localStorage.removeItem(key));
  const sessionKeys = [];
  for (let i = 0; i < window.sessionStorage.length; i += 1) {
    const key = window.sessionStorage.key(i);
    if (key && key.startsWith(`${PENDING_PREFIX}:`)) sessionKeys.push(key);
  }
  sessionKeys.forEach((key) => window.sessionStorage.removeItem(key));
}

export function listLocalConversations(user) {
  return read(user).conversations;
}

export function getLocalConversation(user, id) {
  if (!id) return null;
  const data = read(user);
  const conversation = data.conversations.find((item) => item.id === id);
  if (!conversation && !(data.messages[id] || []).length) return null;
  return { conversation: conversation || { id }, messages: data.messages[id] || [] };
}

export function createDraftConversation(user, { title, text, tool, documentIds } = {}) {
  const id = `local_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  const now = new Date().toISOString();
  const conversation = {
    id,
    title: title || (text || 'New chat').slice(0, 72),
    updated_at: now,
    pending: true,
    tool: tool || null,
    document_ids: Array.isArray(documentIds) ? documentIds.filter(Boolean).slice(0, 8) : [],
  };
  const userMessage = {
    id: `tmp-${id}`,
    role: 'user',
    content: text,
    conversation_id: id,
    created_at: now,
    tool: tool || null,
  };
  const data = read(user);
  data.conversations = [conversation, ...data.conversations.filter((item) => item.id !== id)];
  data.messages[id] = [userMessage];
  write(user, data);
  return { conversation, userMessage };
}

export function replaceDraftConversation(user, oldId, result) {
  if (!result?.conversation_id && !result?.conversation) return;
  const data = read(user);
  const nextId = result.conversation?.id || result.conversation_id;
  const conversation = result.conversation || {
    id: nextId,
    title: (result.user_message?.content || 'Conversation').slice(0, 72),
    updated_at: new Date().toISOString(),
  };
  data.conversations = [conversation, ...data.conversations.filter((item) => item.id !== oldId && item.id !== nextId)];
  const next = [];
  if (result.user_message) next.push(result.user_message);
  if (result.assistant_message) next.push(result.assistant_message);
  data.messages[nextId] = next;
  delete data.messages[oldId];
  write(user, data);
}

export function rememberChat(user, result) {
  if (!result?.conversation && !result?.conversation_id) return;
  const data = read(user);
  const conversation = result.conversation || {
    id: result.conversation_id,
    title: (result.user_message?.content || 'Conversation').slice(0, 72),
    updated_at: new Date().toISOString(),
  };
  data.conversations = [conversation, ...data.conversations.filter((item) => item.id !== conversation.id)];
  const existing = data.messages[conversation.id] || [];
  const next = existing.filter((item) => item.id !== result.user_message?.id && item.id !== result.assistant_message?.id);
  if (result.user_message) next.push(result.user_message);
  if (result.assistant_message) next.push(result.assistant_message);
  data.messages[conversation.id] = next;
  write(user, data);
}

export function rememberBookmark(user, item) {
  const data = read(user);
  data.bookmarks = [item, ...data.bookmarks.filter((row) => row.id !== item.id)];
  write(user, data);
}

export function listLocalBookmarks(user) {
  return read(user).bookmarks;
}

export function removeLocalBookmark(user, id) {
  const data = read(user);
  data.bookmarks = data.bookmarks.filter((row) => row.id !== id);
  write(user, data);
}

export function rememberCheckin(user, item) {
  const data = read(user);
  data.checkins = [item, ...data.checkins.filter((row) => row.id !== item.id)];
  write(user, data);
}

export function listLocalCheckins(user) {
  return read(user).checkins;
}

export function patchLocalCheckin(user, id, updates) {
  const data = read(user);
  data.checkins = data.checkins.map((row) => {
    if (row.id !== id) return row;
    // Full document from API, trust logs array as returned.
    if (Array.isArray(updates?.logs)) {
      const { log: _ignored, ...rest } = updates;
      return { ...row, ...rest, logs: updates.logs.slice(-90) };
    }
    // Append a single log payload when offline / request-shaped.
    if (updates?.log && typeof updates.log === 'object') {
      const entry = {
        severity: Math.max(0, Math.min(10, Math.round(Number(updates.log.severity) || 0))),
        note: String(updates.log.note || '').trim().slice(0, 800),
        recorded_at: updates.log.recorded_at || updates.last_completed_at || new Date().toISOString(),
      };
      if (updates.log.impact != null && updates.log.impact !== '') {
        entry.impact = Math.max(0, Math.min(4, Math.round(Number(updates.log.impact))));
      }
      const logs = [...(row.logs || []), entry].slice(-90);
      const { log: _drop, ...rest } = updates;
      return {
        ...row,
        ...rest,
        logs,
        last_completed_at: entry.recorded_at,
      };
    }
    return { ...row, ...updates };
  });
  write(user, data);
}

export function removeLocalCheckin(user, id) {
  const data = read(user);
  data.checkins = data.checkins.filter((row) => row.id !== id);
  write(user, data);
}

export function rememberAnalysis(user, item) {
  const data = read(user);
  data.analyses = [item, ...data.analyses.filter((row) => row.id !== item.id)];
  write(user, data);
}

export function listLocalAnalyses(user) {
  return read(user).analyses;
}

export function rememberPendingDocuments(user, docs) {
  if (typeof window === 'undefined') return;
  const key = pendingKey(user);
  if (!key) return;
  const next = (Array.isArray(docs) ? docs : [])
    .filter((row) => row && row.id)
    .slice(0, 8)
    .map((row) => ({ id: row.id, filename: row.filename || '' }));
  window.sessionStorage.setItem(key, JSON.stringify(next));
}

export function listPendingDocuments(user) {
  if (typeof window === 'undefined') return [];
  const key = pendingKey(user);
  if (!key) return [];
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed.filter((row) => row && row.id) : [];
  } catch {
    return [];
  }
}

export function clearPendingDocuments(user) {
  if (typeof window === 'undefined') return;
  const key = pendingKey(user);
  if (key) window.sessionStorage.removeItem(key);
}
